import type {
  OfflineSyncPuntosEntregaExportPort,
  OfflineSyncRedencionExportPort,
} from '../domain/ports/offlineSyncExportPorts.ts';
import type {
  OfflineSyncExportBody,
  OfflineSyncPayload,
} from '../domain/offlineSyncPayload.ts';
import type { SyncPdfResolver } from '../domain/ports/SyncPdfResolver.ts';
import {
  mapWirePuntoEntrega,
  mapWireRedencionBundle,
} from './offlineSyncWireMappers.ts';
import {
  normalizeWirePuntoEntrega,
  normalizeWireRedencionBundle,
} from './offlineSyncWireNormalize.ts';

/**
 * Maps the in-memory export snapshot to the wire JSON expected by `SYNC_UPLOAD_*`:
 * participant `publicId` mirrors `participantes.public_id` (numeric JSON when purely digits); `documento` is populated for resolver fallback,
 * MIME prefixes are synthesized for plausible raw Base64 blobs, labels and `tipoBonoNombre` are denormalized.
 */
export async function buildOfflineSyncWirePayload(
  body: OfflineSyncExportBody,
  idempotencyKey: string,
  pe: OfflineSyncPuntosEntregaExportPort,
  rb: Pick<OfflineSyncRedencionExportPort, 'getCatalog'>,
  pdfResolver: SyncPdfResolver,
): Promise<OfflineSyncPayload> {
  const catalog = rb.getCatalog();

  const puntosEntrega = await Promise.all(
    body.puntosEntrega.map(async (p) => {
      const mapped = mapWirePuntoEntrega(pe, catalog, p);
      mapped.informePdf = await pdfResolver('puntoEntrega', p);
      normalizeWirePuntoEntrega(mapped);
      return mapped;
    }),
  );

  const redenciones = await Promise.all(
    body.redenciones.map(async (b) => {
      const mapped = mapWireRedencionBundle(pe, catalog, b);
      mapped.header.informePdf = await pdfResolver('redencionBono', b);
      normalizeWireRedencionBundle(mapped);
      return mapped;
    }),
  );

  return {
    exportedAt: body.exportedAt,
    idempotencyKey,
    puntosEntrega,
    redenciones,
  };
}
